/**
 * Permission access matrix for Role popup (derived from product RBAC spreadsheet).
 * Access values:
 * - yes | no | owner | owner_member | lower_user
 * - workflow_cfs → Comment: C1,2,4,6 & A1,2,5,7 & F1,2,5,7 & S1
 * - workflow_next → Next step: C2,4,6 & A2,5,7 & F2,5,7
 * - request_r123 | request_r1234 → Request action codes (R)
 *
 * ClarityAIR (A) mirrors FossID step numbering (same workflow in app).
 */

export const ACCESS = {
  YES: "yes",
  NO: "no",
  OWNER: "owner",
  OWNER_MEMBER: "owner_member",
  LOWER_USER: "lower_user",
  /** Verification · Comment: C1,2,4,6 & A1,2,5,7 & F1,2,5,7 & S1 */
  WORKFLOW_CF_S: "workflow_cfs",
  /** Verification · Next step: C2,4,6 & A2,5,7 & F2,5,7 */
  WORKFLOW_NEXT: "workflow_next",
  /** Request actions R1–R3 (no approve/reject) */
  REQUEST_R123: "request_r123",
  /** Request actions R1–R4 */
  WORKFLOW_REQUEST: "request_r1234",
};

/** Step numbers for Verification · Comment cells. */
export const WORKFLOW_CFS_CODES = {
  clarity: [1, 2, 4, 6],
  clarity_air: [1, 2, 5, 7],
  fossid: [1, 2, 5, 7],
  sbom: [1],
};

/** Step numbers for Verification · Next step cells. */
export const WORKFLOW_NEXT_CODES = {
  clarity: [2, 4, 6],
  clarity_air: [2, 5, 7],
  fossid: [2, 5, 7],
};

/** All step ids used in the workflow-code legend (spreadsheet Note). */
export const WORKFLOW_LEGEND_STEPS = {
  clarity: [1, 2, 3, 4, 5, 6, 7, 8],
  clarity_air: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  fossid: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  sbom: [1, 2, 3],
  request: [1, 2, 3, 4],
};

export const formatStepCodes = (prefix, numbers) =>
  `${prefix}${numbers.join(",")}`;

export const buildWorkflowCfsTitle = (t) => {
  const lines = [
    ...WORKFLOW_CFS_CODES.clarity.map(
      (n) => `C${n}: ${t(`role.permission-matrix.steps.clarity.${n}`)}`,
    ),
    ...WORKFLOW_CFS_CODES.clarity_air.map(
      (n) => `A${n}: ${t(`role.permission-matrix.steps.clarity_air.${n}`)}`,
    ),
    ...WORKFLOW_CFS_CODES.fossid.map(
      (n) => `F${n}: ${t(`role.permission-matrix.steps.fossid.${n}`)}`,
    ),
    ...WORKFLOW_CFS_CODES.sbom.map(
      (n) => `S${n}: ${t(`role.permission-matrix.steps.sbom.${n}`)}`,
    ),
  ];
  return lines.join("\n");
};

export const buildWorkflowNextTitle = (t) => {
  const lines = [
    ...WORKFLOW_NEXT_CODES.clarity.map(
      (n) => `C${n}: ${t(`role.permission-matrix.steps.clarity.${n}`)}`,
    ),
    ...WORKFLOW_NEXT_CODES.clarity_air.map(
      (n) => `A${n}: ${t(`role.permission-matrix.steps.clarity_air.${n}`)}`,
    ),
    ...WORKFLOW_NEXT_CODES.fossid.map(
      (n) => `F${n}: ${t(`role.permission-matrix.steps.fossid.${n}`)}`,
    ),
  ];
  return lines.join("\n");
};

export const buildRequestCodesTitle = (t, numbers) =>
  numbers
    .map((n) => `R${n}: ${t(`role.permission-matrix.steps.request.${n}`)}`)
    .join("\n");

const Y = ACCESS.YES;
const N = ACCESS.NO;
const O = ACCESS.OWNER;
const OM = ACCESS.OWNER_MEMBER;
const LU = ACCESS.LOWER_USER;
const WCFS = ACCESS.WORKFLOW_CF_S;
const WNEXT = ACCESS.WORKFLOW_NEXT;
const R123 = ACCESS.REQUEST_R123;

/** Shared feature catalog (order = display order). */
export const MATRIX_FEATURES = [
  // Dashboard
  { id: "dashboard", group: "dashboard", feature: "access" },

  // Product
  { id: "product.view", group: "product", feature: "view" },
  { id: "product.add", group: "product", feature: "add_clone" },
  { id: "product.add_project", group: "product", feature: "add_project" },
  { id: "product.assign_group", group: "product", feature: "assign_group" },
  { id: "product.edit", group: "product", feature: "edit" },
  { id: "product.delete", group: "product", feature: "delete" },

  // Project list
  { id: "project.view", group: "project", feature: "view" },
  { id: "project.add", group: "project", feature: "add" },
  { id: "project.edit", group: "project", feature: "edit" },
  { id: "project.manage_member", group: "project", feature: "manage_member" },
  { id: "project.delete", group: "project", feature: "delete" },

  // Verification
  { id: "verification.comment", group: "verification", feature: "comment" },
  { id: "verification.request", group: "verification", feature: "request" },
  { id: "verification.next_step", group: "verification", feature: "next_step" },
  { id: "verification.force_complete", group: "verification", feature: "force_complete" },
  { id: "verification.re_verification", group: "verification", feature: "re_verification" },
  { id: "verification.reject_revoke", group: "verification", feature: "reject_revoke" },

  // SBOM Report
  { id: "sbom_report.view", group: "sbom_report", feature: "view" },
  { id: "sbom_report.add", group: "sbom_report", feature: "add" },
  { id: "sbom_report.edit", group: "sbom_report", feature: "edit" },
  { id: "sbom_report.delete", group: "sbom_report", feature: "delete" },

  // Notice
  { id: "notice.view", group: "notice", feature: "view" },
  { id: "notice.add", group: "notice", feature: "add" },
  { id: "notice.edit", group: "notice", feature: "edit" },
  { id: "notice.delete", group: "notice", feature: "delete" },

  // Comparison / Merge / Preview / Request / Nexus
  { id: "comparison.action", group: "comparison", feature: "action" },
  { id: "sbom_merge.view", group: "sbom_merge", feature: "view" },
  { id: "sbom_merge.add", group: "sbom_merge", feature: "add" },
  { id: "sbom_merge.edit", group: "sbom_merge", feature: "edit" },
  { id: "sbom_merge.delete", group: "sbom_merge", feature: "delete" },
  { id: "preview.view", group: "preview", feature: "view" },
  { id: "preview.add", group: "preview", feature: "add" },
  { id: "preview.edit", group: "preview", feature: "edit" },
  { id: "preview.delete", group: "preview", feature: "delete" },
  { id: "request.view", group: "request", feature: "view" },
  { id: "nexus.import", group: "nexus", feature: "import" },
  { id: "nexus.scan", group: "nexus", feature: "scan" },
  { id: "nexus.allow_block", group: "nexus", feature: "allow_block" },
  { id: "nexus.delete", group: "nexus", feature: "delete" },

  // Open source
  { id: "component.view", group: "component", feature: "view" },
  { id: "component.add", group: "component", feature: "add" },
  { id: "component.edit", group: "component", feature: "edit" },
  { id: "component.delete", group: "component", feature: "delete" },
  { id: "license.view", group: "license", feature: "view" },
  { id: "license.add", group: "license", feature: "add" },
  { id: "license.edit", group: "license", feature: "edit" },
  { id: "license.delete", group: "license", feature: "delete" },

  // Status
  { id: "status.component", group: "status", feature: "component_usage" },
  { id: "status.license", group: "status", feature: "license_usage" },
  { id: "status.vulnerability", group: "status", feature: "vulnerability" },
  { id: "status.cve", group: "status", feature: "cve" },

  // Policy
  { id: "policy.license.view", group: "policy_license", feature: "view" },
  { id: "policy.license.add", group: "policy_license", feature: "add" },
  { id: "policy.license.edit", group: "policy_license", feature: "edit" },
  { id: "policy.license.delete", group: "policy_license", feature: "delete" },
  { id: "policy.vuln.view", group: "policy_vuln", feature: "view" },
  { id: "policy.vuln.add", group: "policy_vuln", feature: "add" },
  { id: "policy.vuln.edit", group: "policy_vuln", feature: "edit" },
  { id: "policy.vuln.delete", group: "policy_vuln", feature: "delete" },
  { id: "policy.asset.view", group: "policy_asset", feature: "view" },
  { id: "policy.asset.add", group: "policy_asset", feature: "add" },
  { id: "policy.asset.edit", group: "policy_asset", feature: "edit" },
  { id: "policy.asset.delete", group: "policy_asset", feature: "delete" },

  // Bulletin (View / Add / Edit / Delete per spreadsheet)
  { id: "bulletin.announcement.view", group: "bulletin", feature: "announcement_view" },
  { id: "bulletin.announcement.add", group: "bulletin", feature: "announcement_add" },
  { id: "bulletin.announcement.edit", group: "bulletin", feature: "announcement_edit" },
  { id: "bulletin.announcement.delete", group: "bulletin", feature: "announcement_delete" },
  { id: "bulletin.qa.view", group: "bulletin", feature: "qa_view" },
  { id: "bulletin.qa.add", group: "bulletin", feature: "qa_add" },
  { id: "bulletin.qa.edit", group: "bulletin", feature: "qa_edit" },
  { id: "bulletin.qa.delete", group: "bulletin", feature: "qa_delete" },
  { id: "bulletin.faq.view", group: "bulletin", feature: "faq_view" },
  { id: "bulletin.faq.add", group: "bulletin", feature: "faq_add" },
  { id: "bulletin.faq.edit", group: "bulletin", feature: "faq_edit" },
  { id: "bulletin.faq.delete", group: "bulletin", feature: "faq_delete" },
  { id: "bulletin.archive.view", group: "bulletin", feature: "archive_view" },
  { id: "bulletin.archive.add", group: "bulletin", feature: "archive_add" },
  { id: "bulletin.archive.edit", group: "bulletin", feature: "archive_edit" },
  { id: "bulletin.archive.delete", group: "bulletin", feature: "archive_delete" },

  // System
  { id: "system.user.view", group: "system_user", feature: "view" },
  { id: "system.user.add", group: "system_user", feature: "add" },
  { id: "system.user.edit", group: "system_user", feature: "edit" },
  { id: "system.user.delete", group: "system_user", feature: "delete" },
  { id: "system.role.view", group: "system_role", feature: "view" },
  { id: "system.role.add", group: "system_role", feature: "add" },
  { id: "system.role.edit", group: "system_role", feature: "edit" },
  { id: "system.role.delete", group: "system_role", feature: "delete" },
  { id: "system.sbom.view", group: "system_sbom", feature: "view" },
  { id: "system.sbom.add", group: "system_sbom", feature: "add" },
  { id: "system.sbom.edit", group: "system_sbom", feature: "edit" },
  { id: "system.sbom.delete", group: "system_sbom", feature: "delete" },
  { id: "system.preferences.view", group: "system_preferences", feature: "view" },
  { id: "system.preferences.edit", group: "system_preferences", feature: "edit" },
  { id: "system.work_history.view", group: "system_work_history", feature: "view" },
];

const allYes = Object.fromEntries(MATRIX_FEATURES.map((f) => [f.id, Y]));
const allNo = Object.fromEntries(MATRIX_FEATURES.map((f) => [f.id, N]));

const baseContributor = {
  ...allNo,
  dashboard: Y,
  "product.view": Y,
  "project.view": OM,
  "verification.comment": WCFS,
  "verification.request": R123,
  "verification.next_step": WNEXT,
  "sbom_report.view": OM,
  "notice.view": OM,
  "comparison.action": Y,
  "sbom_merge.view": O,
  "sbom_merge.add": Y,
  "sbom_merge.edit": O,
  "preview.view": O,
  "preview.add": Y,
  "preview.edit": O,
  "request.view": OM,
  "nexus.import": Y,
  "nexus.scan": Y,
  "component.view": Y,
  "component.add": Y,
  "component.edit": Y,
  "license.view": Y,
  "license.add": Y,
  "license.edit": Y,
  "status.component": OM,
  "status.license": OM,
  "status.vulnerability": OM,
  "status.cve": OM,
  "policy.license.view": Y,
  "policy.vuln.view": Y,
  "policy.asset.view": Y,
  // Spreadsheet: contributors get View/Add/Edit — Delete empty
  "bulletin.announcement.view": Y,
  "bulletin.announcement.add": Y,
  "bulletin.announcement.edit": Y,
  "bulletin.qa.view": Y,
  "bulletin.qa.add": Y,
  "bulletin.qa.edit": Y,
  "bulletin.faq.view": Y,
  "bulletin.faq.add": Y,
  "bulletin.faq.edit": Y,
  "bulletin.archive.view": Y,
  "bulletin.archive.add": Y,
  "bulletin.archive.edit": Y,
};

/** Access map per selectable permission code (excludes SUPERUSER — not shown in Role UI). */
export const MATRIX_BY_PERMISSION = {
  MANAGER: {
    ...allYes,
    // Manager: User = lower-level only; no Role; no Preferences; Work History view
    "system.user.view": LU,
    "system.user.add": LU,
    "system.user.edit": LU,
    "system.user.delete": LU,
    "system.role.view": N,
    "system.role.add": N,
    "system.role.edit": N,
    "system.role.delete": N,
    "system.preferences.view": N,
    "system.preferences.edit": N,
    // SBOM Management: view only for Manager
    "system.sbom.view": Y,
    "system.sbom.add": N,
    "system.sbom.edit": N,
    "system.sbom.delete": N,
    // Policy: view only (edit roles are LICENSE_POLICY_EDIT / VULNERABILITY_POLICY_EDIT)
    "policy.license.add": N,
    "policy.license.edit": N,
    "policy.license.delete": N,
    "policy.vuln.add": N,
    "policy.vuln.edit": N,
    "policy.vuln.delete": N,
    "policy.asset.add": N,
    "policy.asset.edit": N,
    "policy.asset.delete": N,
  },

  PRODUCT_CREATOR: {
    ...baseContributor,
    "product.add": Y,
    "product.delete": Y,
    "product.edit": Y,
    "product.add_project": N,
    "product.assign_group": N,
    // Project List: View O,M only
    "project.add": N,
    "project.edit": N,
    "project.manage_member": N,
    "project.delete": N,
    "verification.force_complete": N,
    "verification.re_verification": N,
    "verification.reject_revoke": N,
    "component.delete": N,
    "license.delete": N,
    "system.user.view": LU,
    "system.user.add": LU,
    "system.user.edit": LU,
    "system.user.delete": LU,
  },

  PRODUCT_MANAGER: {
    ...baseContributor,
    "product.add": N,
    "product.delete": N,
    "product.edit": Y,
    "product.add_project": Y,
    "product.assign_group": Y,
    // Project List: View O,M · Add O · Edit O · Manage ✕ · Delete O
    "project.add": O,
    "project.edit": O,
    "project.manage_member": N,
    "project.delete": O,
    "verification.force_complete": N,
    "verification.re_verification": N,
    "verification.reject_revoke": N,
    "component.delete": N,
    "license.delete": N,
    "system.user.view": LU,
    "system.user.add": LU,
    "system.user.edit": LU,
    "system.user.delete": LU,
  },

  PROJECT_MANAGER: {
    ...baseContributor,
    // Product: no actions
    "product.view": N,
    "product.add": N,
    "product.edit": N,
    "product.delete": N,
    "product.add_project": N,
    "product.assign_group": N,
    // Project List: View O,M · Add O · Edit O · Manage O · Delete ✕
    "project.add": O,
    "project.edit": O,
    "project.manage_member": O,
    "project.delete": N,
    "verification.comment": Y,
    "verification.request": Y,
    "verification.next_step": Y,
    "verification.force_complete": Y,
    "verification.re_verification": Y,
    "verification.reject_revoke": Y,
    // SBOM Report / Notice Info: View O,M · Add/Edit/Delete O
    "sbom_report.add": O,
    "sbom_report.edit": O,
    "sbom_report.delete": O,
    "notice.add": O,
    "notice.edit": O,
    "notice.delete": O,
    // SBOM Merge / Preview: Delete O · Nexus: Allow/Block + Delete ✓
    "sbom_merge.delete": O,
    "preview.delete": O,
    "nexus.allow_block": Y,
    "nexus.delete": Y,
    "component.delete": N,
    "license.delete": N,
    "system.user.view": LU,
    "system.user.add": LU,
    "system.user.edit": LU,
    "system.user.delete": LU,
  },

  DEVELOPER: {
    ...baseContributor,
    "product.view": N,
    "product.add": N,
    "product.edit": N,
    "product.delete": N,
    "product.add_project": N,
    "product.assign_group": N,
    // Project List: View O,M only
    "project.add": N,
    "project.edit": N,
    "project.manage_member": N,
    "project.delete": N,
    "verification.force_complete": N,
    "verification.re_verification": N,
    "verification.reject_revoke": N,
    "component.delete": N,
    "license.delete": N,
    "system.user.view": N,
    "system.user.add": N,
    "system.user.edit": N,
    "system.user.delete": N,
  },

  LICENSE_POLICY_EDIT: {
    ...allNo,
    "policy.license.view": Y,
    "policy.license.add": Y,
    "policy.license.edit": Y,
    "policy.license.delete": Y,
  },

  VULNERABILITY_POLICY_EDIT: {
    ...allNo,
    "policy.vuln.view": Y,
    "policy.vuln.add": Y,
    "policy.vuln.edit": Y,
    "policy.vuln.delete": Y,
    "policy.asset.view": Y,
    "policy.asset.add": Y,
    "policy.asset.edit": Y,
    "policy.asset.delete": Y,
  },

  SBOM_EDIT: {
    ...allNo,
    "system.sbom.view": Y,
    "system.sbom.add": Y,
    "system.sbom.edit": Y,
    "system.sbom.delete": Y,
  },
};

export const MATRIX_GROUP_ORDER = [
  "dashboard",
  "product",
  "project",
  "verification",
  "sbom_report",
  "notice",
  "comparison",
  "sbom_merge",
  "preview",
  "request",
  "nexus",
  "component",
  "license",
  "status",
  "policy_license",
  "policy_vuln",
  "policy_asset",
  "bulletin",
  "system_user",
  "system_role",
  "system_sbom",
  "system_preferences",
  "system_work_history",
];

/**
 * Build grouped rows for one permission code.
 */
export function getMatrixGroupsForPermission(permissionCode, { hideDenied = false } = {}) {
  const accessMap = MATRIX_BY_PERMISSION[permissionCode];
  if (!accessMap) return [];

  const byGroup = new Map();

  for (const feature of MATRIX_FEATURES) {
    const access = accessMap[feature.id] ?? ACCESS.NO;
    if (hideDenied && access === ACCESS.NO) continue;

    if (!byGroup.has(feature.group)) {
      byGroup.set(feature.group, []);
    }
    byGroup.get(feature.group).push({
      id: feature.id,
      feature: feature.feature,
      access,
    });
  }

  return MATRIX_GROUP_ORDER.filter((g) => byGroup.has(g) && byGroup.get(g).length > 0).map(
    (group) => ({
      group,
      rows: byGroup.get(group),
    }),
  );
}

export function hasPermissionMatrix(permissionCode) {
  return Boolean(MATRIX_BY_PERMISSION[permissionCode]);
}

/**
 * Column order for the full comparison matrix (matches Role UI selectable permissions).
 */
export const PERMISSION_MATRIX_COLUMNS = [
  "MANAGER",
  "PRODUCT_CREATOR",
  "PRODUCT_MANAGER",
  "PROJECT_MANAGER",
  "DEVELOPER",
  "LICENSE_POLICY_EDIT",
  "VULNERABILITY_POLICY_EDIT",
  "SBOM_EDIT",
];

/** Full matrix rows for all permission columns (includes denied cells). */
export function getFullPermissionMatrix() {
  const byGroup = new Map();

  for (const feature of MATRIX_FEATURES) {
    const cells = {};
    for (const code of PERMISSION_MATRIX_COLUMNS) {
      cells[code] = MATRIX_BY_PERMISSION[code]?.[feature.id] ?? ACCESS.NO;
    }
    if (!byGroup.has(feature.group)) {
      byGroup.set(feature.group, []);
    }
    byGroup.get(feature.group).push({
      id: feature.id,
      feature: feature.feature,
      cells,
    });
  }

  return MATRIX_GROUP_ORDER.filter((g) => byGroup.has(g) && byGroup.get(g).length > 0).map(
    (group) => ({
      group,
      rows: byGroup.get(group),
    }),
  );
}
